import type React from "react"

import {cn} from "@/lib/cn"

export type CalloutTone = "info" | "warning" | "danger" | "success"

/**
 * An inline message attached to a decision the publisher is making.
 *
 * The tones are load-bearing rather than decorative, and the claim flow uses
 * three of them for genuinely different things:
 *
 *   warning  "another publisher already links this BGG id — continue?"
 *            A soft, allowed path. It must NOT look like an error, because
 *            localized editions sharing a BGG id is legitimate and telling a
 *            publisher off for it is how they stop trusting the warnings.
 *   danger   "that id belongs to a catalog game" — a hard stop with a better
 *            action available (claim it instead).
 *   info     explanation of something that already happened.
 *
 * Colour is never the only signal: each tone carries a text prefix, so the
 * distinction survives a monochrome screenshot and a red-green colour vision
 * deficiency.
 */
const TONES: Record<CalloutTone, {readonly className: string; readonly prefix: string}> = {
  info: {
    className: "border-[color:var(--card-border)] bg-foreground/5 text-foreground",
    prefix: "Note",
  },
  success: {
    className: "border-[color:var(--color-action-foreground)]/30 bg-action/10 text-foreground",
    prefix: "Done",
  },
  warning: {
    className: "border-[color:var(--color-game-accent)]/40 bg-game-accent/10 text-foreground",
    prefix: "Check this",
  },
  danger: {
    className: "border-destructive/50 bg-destructive/10 text-foreground",
    prefix: "Blocked",
  },
}

export const Callout = ({
  tone = "info",
  title,
  children,
  className,
}: {
  tone?: CalloutTone
  title?: string
  children: React.ReactNode
  className?: string
}) => {
  const {className: toneClass, prefix} = TONES[tone]
  return (
    <div
      // "status" rather than "alert": these appear as a result of the
      // publisher's own action and are read after the current utterance
      // finishes, instead of interrupting it.
      role="status"
      className={cn("rounded-mini border p-3 text-sm", toneClass, className)}
    >
      <p className="font-sans text-xs font-bold uppercase tracking-wide text-muted-foreground">
        {title ?? prefix}
      </p>
      <div className="mt-1 flex flex-col gap-2 text-sm">{children}</div>
    </div>
  )
}
